import { BiCardQuery } from '@euler/model/bi/blend';
import { BiDashboardFilters, BiDashboardView } from '@euler/model/bi/dashboard';
import { BiPalette, BiVizSpec } from '@euler/model/bi/viz';
import { memo } from 'react';
import { BiNamePanel } from '../components/BiNamePanel';
import { CardEditorPanel } from './editor/CardEditorPanel';
import { BiEditorTarget } from './editor/functions/biEditorState';
import { PalettePanel } from './palette/PalettePanel';
import { SharingPanel } from './sharing/SharingPanel';
import { RevisionsPanel } from './undo/RevisionsPanel';

/** The one panel the dashboard page has open, if any. */
export type BiDashboardPanel =
  | { kind: 'editor'; target: BiEditorTarget }
  | { kind: 'share' }
  | { kind: 'history' }
  | { kind: 'palette' }
  | { kind: 'rename' };

type SaveOutcome = { ok: true } | { ok: false; message: string };

export const DashboardPanels = memo(
  ({
    dashboard,
    panel,
    filters,
    canManage,
    renaming,
    renameError,
    paletteSaving,
    onSaveCard,
    onPickPalette,
    onUndo,
    onRename,
    onShared,
    onClose,
  }: {
    dashboard: BiDashboardView;
    /** What the cards are viewed under; the editor previews under it. */
    filters: BiDashboardFilters;
    panel: BiDashboardPanel | null;
    canManage: boolean;
    renaming: boolean;
    renameError: string | null;
    paletteSaving: boolean;
    onSaveCard: (
      target: BiEditorTarget,
      input: { spec: BiCardQuery; viz: BiVizSpec; title: string },
    ) => Promise<SaveOutcome>;
    onPickPalette: (palette: BiPalette) => void;
    onUndo: () => void;
    onRename: (name: string) => void;
    onShared: () => void;
    onClose: () => void;
  }) => (
    <>
      <CardEditorPanel
        target={panel?.kind === 'editor' ? panel.target : null}
        filters={filters}
        palette={dashboard.palette}
        onSave={input =>
          panel?.kind === 'editor'
            ? onSaveCard(panel.target, input)
            : Promise.resolve({ ok: true as const })
        }
        onClose={onClose}
      />
      <PalettePanel
        visible={panel?.kind === 'palette'}
        palette={dashboard.palette}
        busy={paletteSaving}
        onPick={onPickPalette}
        onClose={onClose}
      />
      <RevisionsPanel
        visible={panel?.kind === 'history'}
        dashboardId={dashboard.id}
        revision={dashboard.revision}
        onUndo={onUndo}
        onClose={onClose}
      />
      {canManage ? (
        <>
          <SharingPanel
            visible={panel?.kind === 'share'}
            dashboardId={dashboard.id}
            ownerMemberId={dashboard.ownerMemberId}
            visibility={dashboard.visibility}
            onClose={onClose}
            onSaved={onShared}
          />
          <BiNamePanel
            visible={panel?.kind === 'rename'}
            title="重命名看板"
            initialName={dashboard.name}
            confirmTitle="保存"
            error={renameError}
            busy={renaming}
            onConfirm={onRename}
            onClose={onClose}
          />
        </>
      ) : null}
    </>
  ),
);
